import { ref } from "vue";
import ExcelJS from "exceljs";
import { useApolloClient } from "@vue/apollo-composable";
import {
  GetLogsPaginationDocument,
  LogFilterInput,
  LogType,
} from "@/graphql/types";
import { buildExportFileName, displayUTCTimestamp } from "./utils";

const BATCH_SIZE = 20;

const HEADERS = [
  "Type",
  "EventId",
  "IPAddress",
  "MachineName",
  "LogId",
  "Timestamp (UTC)",
  "LogLevel",
  "LogMessage",
  "UserId",
  "Action",
  "ApplicationId",
  "OrganizationId",
  "ApplicationName",
  "OrganizationName",
  "RevisionType",
  "RevisionData",
  "UserInfo",
  "Message",
];

const EXCEL_CELL_MAX_LENGTH = 32767;

function truncateForExcel(value: string | null | undefined): string {
  if (!value) return "";
  if (value.length <= EXCEL_CELL_MAX_LENGTH) return value;
  return value.substring(0, EXCEL_CELL_MAX_LENGTH);
}

function logToRow(log: LogType): unknown[] {
  return [
    log.type ?? "",
    log.eventId ?? "",
    log.ipAddress ?? "",
    log.machineName ?? "",
    log.logId ?? "",
    displayUTCTimestamp(log.timestamp, true),
    log.level ?? "",
    log.eventDescription ?? "",
    log.userId ?? "",
    log.action ?? "",
    log.applicationId ?? "",
    log.organizationId ?? "",
    log.applicationName ?? "",
    log.organizationName ?? "",
    log.revisionType ?? "",
    truncateForExcel(log.revisionData),
    log.userInfo ?? "",
    truncateForExcel(log.message),
  ];
}

async function buildWorkbook(allLogs: LogType[]): Promise<ArrayBuffer> {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Logs");

  const dataRows = allLogs.map(logToRow);

  const headerRow = ws.addRow(HEADERS);
  const addedRows = dataRows.map((row) =>
    ws.addRow(row as ExcelJS.CellValue[])
  );

  headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };

  ws.addTable({
    name: "LogsTable",
    ref: "A1",
    headerRow: true,
    totalsRow: false,
    style: {
      theme: "TableStyleMedium2",
      showRowStripes: true,
    },
    columns: HEADERS.map((h) => ({ name: h, filterButton: true })),
    rows: dataRows as ExcelJS.CellValue[][],
  });

  ws.columns.forEach((col, colIndex) => {
    let maxLen = HEADERS[colIndex]?.length ?? 10;
    for (const row of addedRows) {
      const cell = row.getCell(colIndex + 1);
      const val = cell.value != null ? String(cell.value) : "";
      if (val.length > maxLen) maxLen = val.length;
    }
    col.width = Math.min(maxLen + 2, 60);
  });

  return wb.xlsx.writeBuffer() as Promise<ArrayBuffer>;
}

function triggerDownload(buffer: ArrayBuffer, fileName: string): void {
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

export default function useExportLogsToExcel() {
  const isExporting = ref(false);
  const exportedCount = ref(0);
  const batchFrom = ref(0);
  const finishedCount = ref(0);

  const { client } = useApolloClient();

  async function fetchLogsBatch(
    filter: LogFilterInput,
    page: number
  ): Promise<{ items: LogType[]; total: number }> {
    const result = await client.query({
      query: GetLogsPaginationDocument,
      variables: {
        pageParams: { pageSize: BATCH_SIZE, currentPage: page },
        filter,
      },
      fetchPolicy: "no-cache",
    });
    const data = result?.data?.logsPagination;
    return {
      items: (data?.items ?? []).filter(Boolean) as LogType[],
      total: data?.total ?? 0,
    };
  }

  async function exportAll(filter: LogFilterInput) {
    if (isExporting.value) return;

    isExporting.value = true;
    exportedCount.value = 0;
    batchFrom.value = 0;
    finishedCount.value = 0;

    const allLogs: LogType[] = [];

    try {
      batchFrom.value = 1;
      exportedCount.value = BATCH_SIZE;
      const firstBatch = await fetchLogsBatch(filter, 1);

      if (firstBatch.total === 0) return;

      allLogs.push(...firstBatch.items);
      finishedCount.value = allLogs.length;

      const totalPages = Math.ceil(firstBatch.total / BATCH_SIZE);

      for (let page = 2; page <= totalPages; page++) {
        batchFrom.value = (page - 1) * BATCH_SIZE;
        exportedCount.value = Math.min(page * BATCH_SIZE, firstBatch.total);
        const batch = await fetchLogsBatch(filter, page);
        allLogs.push(...batch.items);
        finishedCount.value = allLogs.length;
      }

      const buffer = await buildWorkbook(allLogs);
      triggerDownload(buffer, buildExportFileName("logs"));
    } finally {
      isExporting.value = false;
    }
  }

  return {
    exportAll,
    isExporting,
    exportedCount,
    batchFrom,
    finishedCount,
  };
}
